import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    const [status, setStatus] = useState('verifying'); // verifying, success, error
    const [message, setMessage] = useState('Verifying your email...');

    const attemptRef = React.useRef(false);

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setMessage('Invalid verification link.');
            return;
        }

        if (attemptRef.current) return;
        attemptRef.current = true;

        const verify = async () => {
            try {
                await axios.post(`${API}/auth/verify-email`, { token });
                setStatus('success');
                setMessage('Your email has been successfully verified! You can now log in.');
            } catch (error) {
                // If the error is "Token invalid" but user is already verified (via a query?), 
                // we might want to be smarter, but for now just showing error is correct if token is consumed.
                // However, since we are preventing double-call locally, this should fix the immediate UI issue.
                setStatus('error');
                setMessage(error.response?.data?.detail || 'Verification failed. Link may be expired or invalid.');
            }
        };

        verify();
    }, [token]);

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <Card className="max-w-md w-full p-8 text-center shadow-lg">
                <div className="flex justify-center mb-6">
                    {status === 'verifying' && <Loader2 className="h-16 w-16 text-blue-600 animate-spin" />}
                    {status === 'success' && <CheckCircle2 className="h-16 w-16 text-green-500" />}
                    {status === 'error' && <XCircle className="h-16 w-16 text-red-500" />}
                </div>

                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    {status === 'verifying' && 'Verifying Email'}
                    {status === 'success' && 'Email Verified'}
                    {status === 'error' && 'Verification Failed'}
                </h1>

                <p className="text-gray-600 mb-8">{message}</p>

                <Button
                    onClick={() => navigate('/')}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                    Go to Login
                </Button>
            </Card>
        </div>
    );
};

export default VerifyEmail;
